import { useFfsAssetModelSnapshot } from 'features/libra/stepComponents/ffs/useFfsAssetModelSnapshot'
import { useRolodexEntriesWithSyncedRefreshes } from 'features/rolodex'
import { useMemo } from 'react'
import { GeckoAssetModel, RolodexEntryType } from 'types'

import { AssetModelDiffGroup, diffAssetModelComponents } from './diffAssetModel'

export interface UseFfsAssetModelDiffResult {
  groups: AssetModelDiffGroup[]
  changeCount: number
  isLoading: boolean
  /** False when there's nothing meaningful to diff (no snapshot, or missing model on either side). */
  canDiff: boolean
}

/**
 * Diffs an FFS ticket's asset-model snapshot against the unit's live master
 * asset model. Mirrors `useFfsAssetModelSnapshot`'s query shapes exactly so
 * SWR dedupes against data the ticket's other asset-model consumers already
 * fetched -- this hook adds no new network traffic on a ticket that already
 * renders the asset-model step.
 */
export function useFfsAssetModelDiff(): UseFfsAssetModelDiffResult {
  const { unitId, snapshotId, isSnapshot, isAssetScoped, isLoading } =
    useFfsAssetModelSnapshot()

  const { entries: snapshotEntries } = useRolodexEntriesWithSyncedRefreshes(
    isAssetScoped && isSnapshot
      ? { type: RolodexEntryType.ASSET_MODEL, ids: [snapshotId] }
      : undefined
  )

  const { entries: liveEntries } = useRolodexEntriesWithSyncedRefreshes(
    unitId
      ? {
          type: RolodexEntryType.ASSET_MODEL,
          tags: { [RolodexEntryType.UNIT]: unitId },
        }
      : undefined
  )

  // Same deterministic id-sorted pick as `resolveTicketAssetModel`, so this
  // hook's "live master" always matches the model the rest of the ticket's
  // read paths resolved.
  const liveMaster = useMemo(() => {
    if (liveEntries.length === 0) return undefined
    const sorted = [...liveEntries].sort((a, b) => a.id.localeCompare(b.id))
    return sorted[0] as GeckoAssetModel
  }, [liveEntries])

  const snapshotEntry = snapshotEntries[0] as GeckoAssetModel | undefined

  const canDiff = isAssetScoped && isSnapshot && !!snapshotEntry && !!liveMaster

  const groups = useMemo<AssetModelDiffGroup[]>(() => {
    if (!canDiff || !snapshotEntry || !liveMaster) return []
    return diffAssetModelComponents(
      liveMaster.raw.data.components as Record<string, unknown>,
      snapshotEntry.raw.data.components as Record<string, unknown>
    )
  }, [canDiff, liveMaster, snapshotEntry])

  const changeCount = useMemo(
    () => groups.reduce((n, g) => n + g.rows.length, 0),
    [groups]
  )

  return useMemo(
    () => ({ groups, changeCount, isLoading, canDiff }),
    [groups, changeCount, isLoading, canDiff]
  )
}
